import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Alert,
} from '@mui/material';
import {
  ManageAccounts as ManageIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import useSubscriptionStore from '../../../store/subscriptionStore';
import { fetchSubscriptionStatus, createPortalSession, createPaygIntent } from '../api/subscriptionApi';
import { formatCredits } from '../../../utils/formatters';
import brand from '../../../config/brand';

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_51Qoix3KoczsQOoB4YvsPWn5b0U6WPBfcht2GXDDbRRjilxgNbfaoQx12jiOeV1WXP3W2ny7wOpy25UUsv08SrFxJ00hwEI4tlJ'
);

const PLAN_COLORS: Record<string, string> = {
  scout: '#78909c',
  cadet: '#42a5f5',
  captain: brand.colors.fire,
  air_marshal: '#ab47bc',
};

const PAYG_PACKS = [
  { key: 'pack_5', amount: 5, credits: '100,000' },
  { key: 'pack_10', amount: 10, credits: '200,000' },
  { key: 'pack_25', amount: 25, credits: '500,000' },
];

/** Inner form for PAYG purchases (needs Stripe context) */
const PaygForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleBuy = async (packKey: string) => {
    setError(null);
    setSelectedPack(packKey);
    setProcessing(true);
    try {
      const { clientSecret: secret } = await createPaygIntent(packKey);
      setClientSecret(secret);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create payment');
      setSelectedPack(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !clientSecret) return;

    setProcessing(true);
    setError(null);
    try {
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) {
        setError('Card information is missing');
        setProcessing(false);
        return;
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setError(result.error.message || 'Payment failed');
      } else if (result.paymentIntent.status === 'succeeded') {
        setSuccess(true);
        setClientSecret(null);
        setSelectedPack(null);
        // Refresh balance
        fetchSubscriptionStatus().catch(() => {});
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        Credits added successfully! Your balance has been updated.
        <Button size="small" onClick={() => setSuccess(false)} sx={{ ml: 1 }}>
          Buy More
        </Button>
      </Alert>
    );
  }

  if (clientSecret) {
    return (
      <Box component="form" onSubmit={handleConfirm}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="subtitle2" gutterBottom>
          Enter card details for {PAYG_PACKS.find(p => p.key === selectedPack)?.credits} credits
        </Typography>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
          <CardElement />
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" onClick={() => { setClientSecret(null); setSelectedPack(null); }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" size="small" disabled={!stripe || processing}>
            {processing ? <CircularProgress size={18} /> : 'Confirm Payment'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={2}>
        {PAYG_PACKS.map((pack) => (
          <Grid item xs={4} key={pack.key}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
              }}
              onClick={() => handleBuy(pack.key)}
            >
              <Typography variant="h6" fontWeight={700}>${pack.amount}</Typography>
              <Typography variant="caption" color="text.secondary">{pack.credits} CP</Typography>
              {processing && selectedPack === pack.key && (
                <CircularProgress size={16} sx={{ display: 'block', mx: 'auto', mt: 1 }} />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/** Main subscription management section */
const SubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const { status, isLoadingStatus } = useSubscriptionStore();
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus().catch(() => {});
  }, []);

  const handleManage = async () => {
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession();
      if (url) window.location.href = url;
    } catch (err: any) {
      console.error('Portal error:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  if (isLoadingStatus && !status) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const planKey = status?.plan || 'scout';
  const planColor = PLAN_COLORS[planKey] || '#78909c';

  return (
    <Box>
      {/* Current Plan Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Your Subscription</Typography>
          <Chip
            label={planKey.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            sx={{ bgcolor: planColor, color: 'white', fontWeight: 600 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Balance</Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatCredits(status?.balance)} CP
            </Typography>
          </Grid>
          {status?.monthlyAllocation != null && status.monthlyAllocation > 0 && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Monthly Allocation</Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatCredits(status.monthlyAllocation)} CP
              </Typography>
            </Grid>
          )}
          {status?.subscriptionStatus && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip
                label={status.subscriptionStatus}
                size="small"
                color={status.subscriptionStatus === 'active' ? 'success' : 'default'}
              />
            </Grid>
          )}
          {status?.currentPeriodEnd && (
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Next Renewal</Typography>
              <Typography variant="body1">
                {new Date(status.currentPeriodEnd).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Box display="flex" gap={2} mt={3}>
          <Button
            variant="outlined"
            startIcon={<ManageIcon />}
            onClick={handleManage}
            disabled={portalLoading || planKey === 'scout'}
          >
            {portalLoading ? <CircularProgress size={20} /> : 'Manage Subscription'}
          </Button>
          <Button variant="contained" onClick={() => navigate('/pricing')}>
            {planKey === 'scout' ? 'Upgrade Plan' : 'Change Plan'}
          </Button>
        </Box>
      </Paper>

      {/* PAYG Top-Up */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CartIcon color="action" />
          <Typography variant="h6">Buy Credit Packs</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Instant top-up for any plan. Credits never expire.
        </Typography>
        <Elements stripe={stripePromise}>
          <PaygForm />
        </Elements>
      </Paper>
    </Box>
  );
};

export default SubscriptionManagement;
